import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useFirebaseUser } from "@/hooks/useFirebaseUser";
import { useFirestoreDoc } from "@/hooks/useFirestoreDoc";
import { usePromise } from "@/hooks/usePromise";
import type { User } from "@skillscratch/shared-types";
// import Radar from "radar-sdk-js";
import posthog from "posthog-js";
import type { LocationAllowedResult } from './LocationContext';
import { LocationContext, LocationType } from './LocationContext';
import { getLocationFrom } from '@/helpers/getLocationFrom';

// const RADAR_KEY = "prj_live_pk_d49a9e0860e8f0cd5d2cc88675596b67494d742f";

const ALLOWED_COUNTRIES: string[] = ["FI", "NL", "HK"];
const ALLOWED_STATES = ["PA", "RI", "NC", "NY", "CT", "NH", "CA", "IL", "VA", "GA", "TX"];

// Radar.initialize(RADAR_KEY);

export interface GoogleLocationResult {
  stateLongName: string | undefined;
  stateShortName: string | undefined;
}

function isLocationAllowed(
  location: string | null,
  locationType: LocationType = LocationType.LocationPermissions
): LocationAllowedResult | undefined {
  if (!location) {
    return undefined;
  }

  // const country = location.addresses.at(0)?.countryCode;

  // if (!country) {
  //   return {
  //     allowed: false,
  //     locationName: "Unknown",
  //   };
  // }

  // if (country && ALLOWED_COUNTRIES.includes(country)) {
  //   return {
  //     allowed: true,
  //     locationName: location.addresses.at(0)?.countryFlag ?? country,
  //   };
  // }

  // if (country !== "US") {
  //   return {
  //     allowed: false,
  //     locationName: country,
  //   };
  // }

  const stateShortName = location;
  const state = stateShortName;

  if (!state) {
    return {
      allowed: false,
      locationName: "Unknown",
      type: locationType
    };
  }

  if (ALLOWED_STATES.includes(state) || ALLOWED_COUNTRIES.includes(state)) {
    return {
      allowed: true,
      locationName: state,
      type: locationType
    };
  }

  return {
    allowed: false,
    locationName: state,
    type: locationType
  };
}

export function LocationProvider({ children }: { children: React.ReactNode }) {
  const [ipGeocodeResponse, isLocationLoading, error, refreshLocation] = usePromise(
    (force) =>
      getLocationFrom(force).then((response) => isLocationAllowed(response)),
    []
  );

  const [manualLocation, setManualLocation] = useState<LocationAllowedResult | undefined>(undefined);

  const user = useFirebaseUser();
  const [userData] = useFirestoreDoc<User>(
    () => {
      const userId = user?.uid;
      if (!userId) {
        return;
      }
      return ["user", userId];
    },
    [user],
    "onDepsChange"
  );

  useEffect(() => {
    if (userData?.isInternal) {
      localStorage.setItem("isInternal", "");
    }
  }, [userData?.isInternal]);

  const locationAllowed = useMemo(() => {
    if (typeof localStorage.getItem("isInternal") === "string") {
      return {
        allowed: true,
        locationName: ipGeocodeResponse?.locationName ?? "XX",
        type: ipGeocodeResponse?.type ?? LocationType.LocationPermissions
      };
    }

    if (ipGeocodeResponse) {
      return ipGeocodeResponse;
    }

    return manualLocation;
  }, [ipGeocodeResponse, manualLocation]);

  const refreshLocationWithInternal = useMemo<typeof refreshLocation>(() => {
    return async (...args) => {
      setManualLocation(undefined);
      
      const result = await refreshLocation(...args);
      if (typeof localStorage.getItem("isInternal") === "string") {
        return {
          allowed: true,
          locationName: result?.locationName ?? "XX",
          type: result?.type ?? LocationType.LocationPermissions
        };
      }
      return result;
    };
  }, [refreshLocation]);

  const setLocation = (location: string, type: LocationType) => {
    const locationResult = isLocationAllowed(location, type);
    console.log('[setLocation]', locationResult);
    setManualLocation(locationResult);
  };

  useEffect(() => {
    posthog.capture("isLocationAllowed", {
      allowed: locationAllowed?.allowed,
      locationName: locationAllowed?.locationName,
      type: locationAllowed?.type
    });
  }, [locationAllowed]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  const value = useMemo(
    () => ({
      locationAllowed,
      isLocationLoading,
      error,
      refreshLocation: refreshLocationWithInternal,
      setLocation,
    }),
    [locationAllowed, error, refreshLocationWithInternal, isLocationLoading]
  );

  return (
    <LocationContext.Provider value={value}>
      {children}
    </LocationContext.Provider>
  );
}